import React, { useRef } from "react";
import { Box, Flex, Text, VisuallyHidden } from "@chakra-ui/react";
import { gsap } from "gsap";
import { ScrambleTextPlugin } from "gsap/dist/ScrambleTextPlugin";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { SplitText } from "gsap/dist/SplitText";
import { useGSAP } from "@gsap/react";
import { Section } from "components/section";
import { TextTitle } from "ui/text";
import { Container } from "ui/container";
import { registerGsapPlugins } from "lib/gsap";
import { AidSisyphusMountain } from "./mountain";

const decisions = ["What content should I send?", "In what channel?", "At what time?", "To which people?"];

registerGsapPlugins(ScrambleTextPlugin, ScrollTrigger, SplitText);

export function AidSisyphus() {
  const ref = useRef<HTMLDivElement>(null);
  const timeline = useRef<GSAPTimeline>();

  useGSAP(
    () => {
      const scrambleText = new SplitText(".aid-text-scramble", {
        type: "chars",
        charsClass: "char",
        position: "relative",
      });
      timeline.current = gsap.timeline({
        scrollTrigger: "#aid-scramble-container",
        delay: 0.4,
      });
      timeline.current.from(
        ".aid-text-scramble .char",
        {
          duration: 0.75,
          stagger: 0.025,
          scrambleText: { text: "x", chars: "lowerCase", speed: 0.2, delimiter: " ", tweenLength: false },
          ease: "expo",
        },
        0.5,
      );
    },
    { scope: ref },
  );

  return (
    <Box ref={ref}>
      <Section
        fullWidth
        noContainerPx
        id="aid-scramble-container"
        bg="new.violet.150"
        themeBgColor="new.violet.150"
        pos="relative"
        overflow="hidden"
        pb={{ base: "biggest", md: "huge" }}
      >
        <Container
          h={{ base: "94vh", md: "100vh" }}
          pl={{ base: "containerPx.mobile", md: "huge", xl: "0" }}
          maxH={{ base: "640px", sm: "600px", md: "660px", xl: "680px" }}
        >
          <TextTitle
            pos="relative"
            zIndex={1}
            maxW="lg"
            size="md"
            title="Sometimes, the job of lifecycle marketing feels impossible"
            description="Manual journeys and A/B tests can't deliver personalization at scale. Every message turns into a high-stakes decision with no clear answer."
          />
          <Flex
            color="brand.textSecondary.light"
            flexDir="column"
            gap="small"
            maxW="5xl"
            mt={{ base: "big", sm: "biggest" }}
            mb="bigger"
          >
            {decisions.map((decision, index) => (
              <Flex
                key={decision}
                align="center"
                color="new.violet.950"
                gap="smallest"
                ml={`${(decisions.length - index) * 24}px`}
              >
                <VisuallyHidden>{decision}</VisuallyHidden>
                <Text
                  id={`aid-text-${index + 1}`}
                  aria-hidden="true"
                  className="aid-text-scramble"
                  color="brand.textSecondary.light"
                  textStyle={{ base: "overline2", md: "overline1" }}
                  whiteSpace="nowrap"
                  pos="relative"
                  zIndex={1}
                >
                  {decision}
                </Text>
                <Box aria-hidden="true" flex={1} height="1px" bg="new.gray.300" pos="relative" />
              </Flex>
            ))}
          </Flex>
        </Container>
        <AidSisyphusMountain
          aria-hidden="true"
          userSelect="none"
          pos="absolute"
          bottom="-6px"
          left={{ base: "-10%", md: "0" }}
          right={{ base: "-10%", xl: "-24%" }}
          top="auto"
          maxW={{ base: "1700px", md: "1900px", xl: "2040px" }}
          mx="auto"
        />
      </Section>
    </Box>
  );
}
